import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Phone, 
  FileText, 
  MapPin,
  CheckCircle,
  Clock,
  Car
} from 'lucide-react';

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5 }
};

const ZonesPage = () => {
  const primaryZone = {
    name: "Seine-et-Marne (77)",
    cities: [
      "Moissy-Cramayel",
      "Melun",
      "Sénart",
      "Lieusaint",
      "Savigny-le-Temple",
      "Combs-la-Ville",
      "Pontault-Combault",
      "Brie-Comte-Robert",
      "Roissy-en-Brie",
      "Ozoir-la-Ferrière",
      "Cesson",
      "Vert-Saint-Denis",
      "Dammarie-les-Lys",
      "Fontainebleau",
      "Meaux",
      "Chelles",
      "Torcy",
      "Champs-sur-Marne",
      "Bussy-Saint-Georges",
      "Serris",
      "Provins",
      "Montereau-Fault-Yonne",
      "Lognes",
      "Noisiel",
      "Le Mée-sur-Seine",
      "Lagny-sur-Marne",
      "Villeparisis",
      "Mitry-Mory",
      "Réau",
      "Nandy"
    ]
  };

  const secondaryZones = [
    {
      name: "Essonne (91)",
      cities: ["Évry-Courcouronnes", "Corbeil-Essonnes", "Massy", "Savigny-sur-Orge", "Grigny", "Viry-Châtillon", "Ris-Orangis", "Brunoy", "Yerres", "Montgeron"]
    },
    {
      name: "Val-de-Marne (94)",
      cities: ["Créteil", "Vitry-sur-Seine", "Champigny-sur-Marne", "Saint-Maur-des-Fossés", "Maisons-Alfort", "Alfortville", "Villeneuve-Saint-Georges", "Choisy-le-Roi", "Thiais", "Ormesson-sur-Marne"]
    },
    {
      name: "Paris (75)",
      cities: ["Tous les arrondissements"]
    },
    {
      name: "Seine-Saint-Denis (93)",
      cities: ["Montreuil", "Saint-Denis", "Aulnay-sous-Bois", "Drancy", "Noisy-le-Grand", "Pantin", "Bondy", "Livry-Gargan", "Sevran", "Bobigny"]
    }
  ];

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="bg-[#F8FAFC] py-16 lg:py-20" data-testid="zones-hero">
        <div className="container-custom">
          <motion.div 
            className="text-center max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <span className="badge-primary mb-4">Zone d'intervention</span>
            <h1 className="font-outfit text-3xl md:text-4xl lg:text-5xl font-bold text-[#000000] mt-4 mb-6">
              Climaticien basé en Seine-et-Marne, intervention France entière
            </h1>
            <p className="text-slate-600 text-base lg:text-lg">
              ADM Confort Clim, installateur climatisation et frigoriste basé à Moissy-Cramayel (77). Nous intervenons en Île-de-France et dans toute la France selon vos besoins : pose, dépannage et entretien de climatisation, pompe à chaleur et froid commercial.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Key Info */}
      <section className="py-10 bg-[#1997b0]" data-testid="zones-key-info">
        <div className="container-custom">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: MapPin, title: "Notre siège", text: "Moissy-Cramayel (77550)" },
              { icon: Car, title: "Rayon d'intervention", text: "Île-de-France + France entière" },
              { icon: Clock, title: "Disponibilité", text: "Dépannage 24h/24 - 7j/7" }
            ].map((item, index) => (
              <motion.div 
                key={index}
                className="flex items-center gap-4 text-white"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <item.icon size={22} />
                </div>
                <div>
                  <div className="font-semibold text-sm">{item.title}</div>
                  <div className="text-white/80 text-sm">{item.text}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Primary Zone */}
      <section className="py-16 lg:py-24 bg-white" data-testid="zones-primary">
        <div className="container-custom">
          <motion.div 
            className="mb-10"
            {...fadeInUp}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-[#1997b0] rounded-full flex items-center justify-center">
                <MapPin className="text-white" size={18} />
              </div>
              <h2 className="font-outfit text-xl md:text-2xl font-bold text-[#000000]">
                Seine-et-Marne (77) - Siège social
              </h2>
            </div>
            <p className="text-slate-600 text-sm max-w-2xl">
              Siège social à Moissy-Cramayel, nous intervenons rapidement en Île-de-France pour la pose, le dépannage et l'entretien de climatisation, pompe à chaleur et froid commercial.
            </p>
          </motion.div>

          <motion.div 
            className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            {primaryZone.cities.map((city, index) => (
              <motion.div
                key={index}
                className="bg-[#F8FAFC] px-4 py-3 rounded-xl flex items-center gap-2 hover:bg-[#1997b0]/10 transition-colors"
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.02 }}
                data-testid={`zone-city-77-${index}`}
              >
                <CheckCircle className="text-[#1997b0] flex-shrink-0" size={14} />
                <span className="text-slate-700 text-sm">{city}</span>
              </motion.div>
            ))}
          </motion.div>

          <motion.div 
            className="mt-8 bg-[#fabc63]/10 p-5 rounded-xl"
            {...fadeInUp}
          >
            <div className="flex items-center gap-3">
              <Clock className="text-[#fabc63]" size={22} />
              <div>
                <h3 className="font-outfit font-semibold text-[#000000] text-sm">Urgence climatisation Seine-et-Marne</h3>
                <p className="text-slate-600 text-xs">Dépannage climatisation et chambre froide 24h/24 - 7j/7 sur tout le département</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Secondary Zones */}
      <section className="py-16 lg:py-24 bg-[#F8FAFC]" data-testid="zones-secondary">
        <div className="container-custom">
          <motion.div 
            className="text-center mb-10"
            {...fadeInUp}
          >
            <h2 className="font-outfit text-xl md:text-2xl font-bold text-[#000000] mb-3">
              Intervention Île-de-France et France entière
            </h2>
            <p className="text-slate-600 text-sm max-w-xl mx-auto">
              Au-delà de la Seine-et-Marne, nous intervenons en Île-de-France et dans toute la France selon vos projets de climatisation, pompe à chaleur et froid commercial.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {secondaryZones.map((zone, zoneIndex) => (
              <motion.div
                key={zoneIndex}
                className="bg-white p-5 rounded-xl shadow-sm"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: zoneIndex * 0.1 }}
                data-testid={`zone-secondary-${zoneIndex}`}
              >
                <h3 className="font-outfit text-base font-semibold text-[#000000] mb-3 flex items-center gap-2">
                  <MapPin className="text-[#1997b0]" size={16} />
                  {zone.name}
                </h3>
                <div className="flex flex-wrap gap-2">
                  {zone.cities.map((city, cityIndex) => (
                    <span 
                      key={cityIndex}
                      className="bg-[#F8FAFC] px-3 py-1 rounded-full text-xs text-slate-600"
                    >
                      {city}
                    </span>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services by zone */}
      <section className="py-16 lg:py-24 bg-white" data-testid="zones-services">
        <div className="container-custom">
          <motion.div 
            className="text-center mb-10"
            {...fadeInUp}
          >
            <h2 className="font-outfit text-xl md:text-2xl font-bold text-[#000000] mb-3">
              Nos services partout en France
            </h2>
            <p className="text-slate-600 text-sm max-w-xl mx-auto">
              Installateur climatisation et frigoriste pour particuliers et professionnels.
            </p>
          </motion.div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              {
                title: "Pose climatisation",
                description: "Installation climatisation split, multi-split et gainable"
              },
              {
                title: "Pompe à chaleur",
                description: "Installation et dépannage PAC air/eau"
              },
              {
                title: "Froid commercial",
                description: "Chambre froide et vitrine réfrigérée"
              },
              {
                title: "Dépannage urgent",
                description: "Intervention 24h/24 - 7j/7 France entière"
              }
            ].map((service, index) => (
              <motion.div
                key={index}
                className="card-service"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <h3 className="font-outfit text-sm font-semibold text-[#000000] mb-2">{service.title}</h3>
                <p className="text-slate-600 text-xs">{service.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 bg-[#1997b0]" data-testid="zones-cta">
        <div className="container-custom">
          <motion.div 
            className="text-center text-white"
            {...fadeInUp}
          >
            <h2 className="font-outfit text-2xl md:text-3xl font-bold mb-3">
              Besoin d'un climaticien ?
            </h2>
            <p className="text-white/80 mb-6 text-sm max-w-xl mx-auto">
              Basés en Seine-et-Marne, nous intervenons partout en France. Contactez-nous pour un devis gratuit !
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a 
                href="tel:0603054911"
                className="btn-emergency flex items-center gap-2"
                data-testid="zones-cta-call"
              >
                <Phone size={18} />
                <span>06 03 05 49 11</span>
              </a>
              <Link 
                to="/contact"
                className="bg-white text-[#1997b0] hover:bg-slate-100 transition-all duration-300 rounded-full px-6 py-3 font-semibold flex items-center gap-2 text-sm"
                data-testid="zones-cta-contact"
              >
                <FileText size={18} />
                <span>Devis gratuit</span>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ZonesPage;
